import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import PhoneButton, { PHONE_HREF } from "@/components/shared/PhoneButton";

interface CTAButtonsProps {
  primaryCTA?: { label: string; href: string };
  secondaryCTA?: { label: string; href: string };
  /** Extra wrapper classes (e.g. justify-center vs justify-start) */
  className?: string;
}

/**
 * Standardised dual-CTA (Book Now + Call Now) used in heroes and page-bottom banners.
 * Uses consistent pill styling with CalendarDays bounce icon.
 */
export default function CTAButtons({
  primaryCTA = { label: "Book Now", href: "/book" },
  secondaryCTA = { label: "Call Now", href: PHONE_HREF },
  className = "",
}: CTAButtonsProps) {
  const primaryClasses =
    "group cta-hover inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-10 py-3 rounded-full font-display uppercase tracking-[0.2em] text-sm transition-all";

  const outlineClasses =
    "cta-hover-outline inline-flex items-center justify-center border border-foreground/30 text-foreground px-10 py-3 rounded-full font-display uppercase tracking-[0.2em] text-sm transition-all";

  const isExternal = (href: string) =>
    href.startsWith("tel:") || href.startsWith("http");

  return (
    <div className={`flex flex-col sm:flex-row items-center gap-4 ${className}`}>
      {/* Primary */}
      {isExternal(primaryCTA.href) ? (
        <a href={primaryCTA.href} className={primaryClasses}>
          <CalendarDays className="h-4 w-4 bounce-icon" />
          <span>{primaryCTA.label}</span>
        </a>
      ) : (
        <Link to={primaryCTA.href} className={primaryClasses}>
          <CalendarDays className="h-4 w-4 bounce-icon" />
          <span>{primaryCTA.label}</span>
        </Link>
      )}

      {/* Secondary */}
      {secondaryCTA.href === PHONE_HREF ? (
        <PhoneButton variant="pill-outline" showNumber={false} label={secondaryCTA.label} />
      ) : isExternal(secondaryCTA.href) ? (
        <a href={secondaryCTA.href} className={outlineClasses}>
          <span>{secondaryCTA.label}</span>
        </a>
      ) : (
        <Link to={secondaryCTA.href} className={outlineClasses}>
          <span>{secondaryCTA.label}</span>
        </Link>
      )}
    </div>
  );
}
